import { type FunctionComponent, type ReactNode, useState } from 'react';

import {
  type UseAsyncDataResT,
  assertEmptyObject,
} from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { AdminPractitionerSelect } from 'components/admin';

import { useArtist } from 'hooks';

import { updateVideoData, uploadVideo } from 'services/video';

import {
  type ArtistT,
  MDASH,
  type VideoStatusT,
  type VideoT,
  areEqualArraySets,
  getId,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import IconClose from 'assets/images/icon-cross-24px.svg';
import IconEdit from 'assets/images/icon-edit-16px.svg';

import { Footer } from './Footer';
import Header, { type TabT } from './Header';
import SpecsTab, { type VideoSpecsT } from './SpecsTab';
import UploadTab from './UploadTab';

import S from './style.scss';

/** Evaluates whether the data carried in the given `specs` object are different
 *  from the related data stored inside video data envelope. */
// TODO: Not sure about a good way to reduce this function's complexity.
// Not that important anyway, but a nice brain teaser to think about.
// eslint-disable-next-line complexity
function areSpecsDirty({
  aiAnalysis,
  bodyAreas,
  customBodyAreas,
  customEquipmentRequirements,
  customModalities,
  difficultyLevels,
  environmentNeeds,
  equipmentNeedsV2,
  intent,
  modalities,
  title,
  userSummary,
  ...rest
}: VideoSpecsT, video: UseAsyncDataResT<VideoT>): boolean {
  assertEmptyObject(rest);

  const { data } = video;
  return aiAnalysis !== data?.aiAnalysis
    || userSummary !== data?.userSummary
    || environmentNeeds !== data?.environmentNeeds
    || title !== data?.title
    || intent !== data?.intent
    || !areEqualArraySets(customBodyAreas, data?.customBodyAreas)
    || !areEqualArraySets(
      customEquipmentRequirements,
      data?.customEquipmentRequirements,
    )
    || !areEqualArraySets(customModalities, data?.customModalities)
    || !areEqualArraySets(equipmentNeedsV2, data?.equipmentNeedsV2)
    || !areEqualArraySets(difficultyLevels, data?.difficultyLevels)
    || !areEqualArraySets(bodyAreas, data?.bodyAreas)
    || !areEqualArraySets(modalities, data?.modalities);
}

/** Evaluates if we are ready to permit the user to go to the "Specs" tab,
 *  or not. */
function canGoToSpecs(
  localVideo: File | string | undefined,
  video: UseAsyncDataResT<VideoT>,
): boolean {
  return !!(localVideo ?? video.data);
}

function isSet(value: unknown): boolean {
  return !!(value && (!Array.isArray(value) || value.length));
}

/** Evaluates if the combination of local edits and existing video (if any)
 *  can be published. */
function canPublish(
  localVideo: File | string | undefined,
  localSpecs: VideoSpecsT,
  areLocalSpecsDirty: boolean,
  video: UseAsyncDataResT<VideoT>,
): boolean {
  const { data } = video;

  /**
   * It requires both AI video description to be added,
   * and the extraction of meta data from description performed
   * priorly to publishing the video.
   */
  if (data?.aiAnalysisState !== 'DONE') return false;

  /**
   * Returns "true" if the given spec is not set neither in the local specs,
   * nor in the video object; otherwise returns "false".
   */
  const notSet = (
    key: keyof VideoSpecsT,
  ): boolean => !isSet(localSpecs[key]);

  // The following "ifs" check whether we have all data necessary to publish
  // a video, either in the local state, or in the existing video object.
  // NOTE: These are separate "ifs" as a single logical expression looks too
  // cumbersome to understand at glance and modify without mistakes.
  if (!localVideo && !data.url) return false;

  if (
    notSet('bodyAreas')
    || notSet('difficultyLevels')
    || notSet('environmentNeeds')
    || notSet('intent')
    || notSet('modalities')
    || notSet('title')
  ) return false;

  // Now we check if there are any data that actually changed, or there is
  // nothing new to save.
  return !!(((localVideo ?? areLocalSpecsDirty)) || (data.status !== 'Published'));
}

/** Evaluates if the combination of local edits and existing video (if any)
 *  can be saved as draft. */
function canSaveAsDraft(
  localVideo: File | string | undefined,
  areLocalSpecsDirty: boolean,
  video: UseAsyncDataResT<VideoT>,
): boolean {
  return !!(((localVideo ?? areLocalSpecsDirty)) || (
    video.data?.status !== undefined
    && video.data.status !== 'Draft'
  ));
}

/**
 * Extracts VideoSpecsT from given VideoT, or falls back to empty object
 * if now video object is given.
 */
function getSpecs(video: VideoT | null): VideoSpecsT {
  return {
    aiAnalysis: video?.aiAnalysis,
    bodyAreas: video?.bodyAreas ?? [],
    customBodyAreas: video?.customBodyAreas ?? [],
    customEquipmentRequirements: video?.customEquipmentRequirements ?? [],
    customModalities: video?.customModalities ?? [],
    difficultyLevels: video?.difficultyLevels ?? [],
    environmentNeeds: video?.environmentNeeds,
    equipmentNeedsV2: video?.equipmentNeedsV2 ?? [],
    intent: video?.intent,
    modalities: video?.modalities ?? [],
    title: video?.title,
    userSummary: video?.userSummary,
  };
}

type PropsT = {
  userArtist: UseAsyncDataResT<ArtistT>;
  video: UseAsyncDataResT<VideoT>;
};

/** The main video editor assembly (while the parent <CmsEditVideoPage>
 *  component takes care to load the most crucial dependency data, and
 *  verify user permissions / render error messages, if any). */
// TODO: This is true, we should refactor this component for similicity,
// but we don't have time, nor urgency to deal with it right now.
// eslint-disable-next-line complexity
const Editor: FunctionComponent<PropsT> = ({ userArtist, video }) => {
  const [busy, setBusy] = useState<boolean | number>(false);
  const [tab, setTab] = useState<TabT>('UPLOAD');

  const [perms] = useGlobalState('user.permissions');
  if (!perms) throw Error('Unathorized');

  const artistId = video.data?.artistId;

  // TODO: Here is something off, it looks like falling back to
  // non-empty ID with disabled flag set still attempts to load
  // data from the backend. While empty ID leads it to return
  // entire collection (which leads to the result different from TS type).
  // I guess, it might be a bug in react-global-state library.
  // Perhaps "disabled" option does not work correctly for the collection,
  // at least in some cases.
  const artist = useArtist(artistId ?? '', {
    disabled: artistId === undefined,
  });

  const [newOwner, setNewOwner] = useState<ArtistT>();
  const [showCreatorPanel, setShowCreatorPanel] = useState(false);

  const [
    localSpecs,
    setLocalSpecs,
  ] = useState<VideoSpecsT>(getSpecs(video.data));

  const [localVideo, setLocalVideo] = useState<File | string>();

  const vAreLocalSpecsDirty = areSpecsDirty(localSpecs, video) || (
    !!newOwner && getId(newOwner) !== artistId
  );

  const vCanGoToSpecs = canGoToSpecs(localVideo, video);

  const vCanPublish = canPublish(
    localVideo,
    localSpecs,
    vAreLocalSpecsDirty,
    video,
  );

  const vCanSaveAsDraft = canSaveAsDraft(
    localVideo,
    vAreLocalSpecsDirty,
    video,
  );

  const gs = useGlobalStateObject();

  /**
   * Saves the video (including its uploading to Vimeo, and updates of video
   * information in our DB), creating a new one, if necessary. It also operates
   * necessary UI updates to indicate the update state and its progress.
   */
  const save = async (
    status: VideoStatusT,
    runAiAnalysis?: boolean,
  ) => {
    try {
      setBusy(0);

      let res = video.data;

      // 1. Video content (re-)upload.
      if (localVideo) {
        // For now, we only support uploading of local files (and not the video
        // import from a given remote URL).
        if (!(localVideo instanceof File)) throw Error('Not implemented yet');

        const artistId2 = res?.artistId ?? userArtist.data?.id;
        if (artistId2 === undefined) throw Error('Internal error');

        // eslint-disable-next-line require-atomic-updates
        res = await uploadVideo({
          artistId: artistId2,
          file: localVideo,
          onProgress: (value) => {
            setBusy(Math.round(100 * value));
          },
          videoId: res?.id,
        });
      }

      // We cannot save video details prior to its source uploaded,
      // and the record created in our database.
      if (!res?.id) throw Error('Internal error');

      // 2. Update of video details in our DB.
      if (vAreLocalSpecsDirty || status !== res.status || runAiAnalysis) {
        // eslint-disable-next-line require-atomic-updates
        res = await updateVideoData(res.id, {
          status,
          ...localSpecs,
          artistId: newOwner?.id,
        }, runAiAnalysis);

        setLocalSpecs(getSpecs(res));
      }

      setLocalVideo(undefined);
      await video.reload(() => res);

      const gsv = gs.get('videos.data.items');
      if (gsv) {
        const idx = gsv.findIndex(({ id }) => id === res.id);
        if (idx >= 0) gs.set(`videos.data.items.${idx}`, res);
        else gs.set('videos.data.items', [res, ...gsv]);
      }
    } catch {
      // TODO: Wire up a nice error message modal later.
      // eslint-disable-next-line no-alert
      alert('Failed to save the draft!');

      // NOTE: Let's keep it simple, and just reload the page in case of error,
      // this way we ensure what user will see is consistent with the database
      // content (as a failed saving still may save a subset of data).
      location.reload();
    } finally {
      setBusy(false);
    }
  };

  let content: ReactNode;
  switch (tab) {
    case 'SPECS':
      content = (
        <SpecsTab
          aiAnalysisState={video.data?.aiAnalysisState}
          onRunAiAnalysis={() => {
            void save(video.data?.status ?? 'Draft', true);
          }}
          setSpecs={setLocalSpecs}
          specs={localSpecs}
          submitted={!!video.data}
          video={video.data}
        />
      );
      break;
    case 'UPLOAD':
      content = (
        <UploadTab
          localVideo={localVideo}
          setLocalVideo={setLocalVideo}
          video={video.data}
        />
      );
      break;
    default: throw Error('Unexpected case');
  }

  // NOTE: Regular practitioners only have access to their own videos within
  // the admin app; however, users with admin privileges see all videos within
  // the platform, thus we show them who is the creator of the video they are
  // looking at.
  //
  // NOTE: `artistId` equal "undefined" means we are uploading a brand new video;
  // otherwise we are editing details of a previously uploaded one.
  let videoOwnerBlock: ReactNode;
  if (perms.EDIT_ANY_VIDEO_DETAILS && artistId !== undefined) {
    if (artist.loading) videoOwnerBlock = <Throbber />;
    else if (artist.data) {
      const currentArtist = newOwner ?? artist.data;

      videoOwnerBlock = (
        <div styleName="creatorInfo">
          <span>Created by: </span>
          <span styleName="creatorName">{currentArtist.user.firstName} {currentArtist.user.lastName}</span>
          {perms.EDIT_VIDEO_OWNERSHIP ? (
            <button
              onClick={() => {
                setShowCreatorPanel(true);
              }}
              styleName="editButton"
              type="button"
            >
              <IconEdit />
            </button>
          ) : null}
        </div>
      );
    } else videoOwnerBlock = MDASH;

    const notSaved = newOwner && getId(newOwner) !== artist.data?.id;

    let pracitionerClassName = S.practitioner;
    if (notSaved) pracitionerClassName += ` ${S.withRedBorder}`;

    videoOwnerBlock = (
      <div styleName="practitionerContainer">
        <div className={pracitionerClassName}>
          {videoOwnerBlock}
        </div>
        {notSaved ? <div styleName="notYetSaved">Not yet saved!</div> : null}
      </div>
    );

  // For the newly uploaded videos priviledged admin users can select who
  // will own it.
  } else if (perms.EDIT_VIDEO_OWNERSHIP && artistId === undefined) {
    if (userArtist.loading) videoOwnerBlock = <Throbber />;

    // TODO: This is not strictly correct, but for now only artists can upload
    // new videos, so if an admin can arrive here, it is already guaranteed that
    // he is a practitioner as well... Though, to be clean we need to handle
    // gracefully that there might be no userArtist, and <AdminPracitionerSelect>
    // should handle an empty selection.
    else if (userArtist.data) {
      const currentArtist = newOwner ?? userArtist.data;

      videoOwnerBlock = (
        <div styleName="creatorInfo">
          <span>Upload as: </span>
          <span styleName="creatorName">{currentArtist.user.firstName} {currentArtist.user.lastName}</span>
          <button
            onClick={() => {
              setShowCreatorPanel(true);
            }}
            styleName="editButton"
            type="button"
          >
            <IconEdit />
          </button>
        </div>
      );
    }

    videoOwnerBlock = (
      <div styleName="practitionerContainer">
        <div styleName="practitioner">
          {videoOwnerBlock}
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        canGoToSpecs={vCanGoToSpecs}
        setTab={setTab}
        tab={tab}
      />
      {videoOwnerBlock}
      {content}
      <Footer
        canGoToSpecs={vCanGoToSpecs}
        canPublish={vCanPublish}
        canSaveAsDraft={vCanSaveAsDraft}
        onSave={(status) => {
          void save(status);
        }}
        setTab={setTab}
        tab={tab}
        videoId={video.data?.id}
      />
      {busy === false ? null : (
        <Throbber
          progress={typeof busy === 'number' ? busy : undefined}
          withOverlay
        />
      )}
      {showCreatorPanel && perms.EDIT_VIDEO_OWNERSHIP ? (
        <>
          <div
            onClick={() => {
              setShowCreatorPanel(false);
            }}
            onKeyDown={() => {
              setShowCreatorPanel(false);
            }}
            role="presentation"
            styleName="backdrop"
          />
          <div styleName="creatorPanel">
            <div styleName="panelHeader">
              <h2 styleName="panelTitle">Change Creator</h2>
              <button
                onClick={() => {
                  setShowCreatorPanel(false);
                }}
                styleName="closeButton"
                type="button"
              >
                <IconClose />
              </button>
            </div>

            <div styleName="panelContent">
              <div styleName="selectGroup">
                <div styleName="selectLabel">Select creator</div>
                <AdminPractitionerSelect
                  noTransferConfirmation
                  onChange={(p) => {
                    if (p && !Array.isArray(p)) {
                      setNewOwner(p);
                      setShowCreatorPanel(false);
                    }
                  }}
                  practitioner={
                    newOwner
                    ?? (artistId ? artist.data : userArtist.data)
                    ?? undefined
                  }
                />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default Editor;
